import type { ApiResponse, ErrorCode, LoginRequest, LoginResponse, AuthUser, FieldMeta } from '@crm/shared'
import useSWR from 'swr'
import { useAuthStore } from '../stores/auth.store'

const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1'

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: ErrorCode | 'UNKNOWN_ERROR',
    message: string,
  ) {
    super(message)
    this.name = 'ApiClientError'
  }
}

type ErrorPayload = {
  error?: {
    code?: ErrorCode
    message?: string
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const token = useAuthStore.getState().token
  const headers = new Headers(init?.headers)
  if (!headers.has('Content-Type') && init?.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const res = await fetch(`${API_BASE}${url}`, { ...init, headers })

  if (res.status === 401) {
    useAuthStore.getState().clearAuth()
  }
  if (res.status === 403) {
    window.dispatchEvent(new CustomEvent('crm:forbidden'))
  }
  if (res.status >= 500) {
    window.dispatchEvent(new CustomEvent('crm:server-error', { detail: { status: res.status } }))
  }

  if (!res.ok) {
    const err = await res.json().catch<ErrorPayload>(() => ({ error: { message: res.statusText } }))
    throw new ApiClientError(
      res.status,
      err.error?.code ?? 'UNKNOWN_ERROR',
      err.error?.message || err.error?.code || 'Request failed',
    )
  }

  return res.json().catch(() => {
    throw new ApiClientError(res.status, 'UNKNOWN_ERROR', 'Invalid response body')
  })
}

export function useApi<T>(path: string | null) {
  const token = useAuthStore((state) => state.token)
  const swrKey = path ? [path, token] as const : null

  return useSWR<T, ApiClientError>(
    swrKey,
    ([requestPath]) => request<T>(requestPath),
    {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 2000,
    },
  )
}

export async function apiGet<T>(path: string): Promise<T> {
  return request<T>(path)
}

export async function apiPost<TResponse, TBody>(path: string, body: TBody): Promise<TResponse> {
  return request<TResponse>(path, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function apiPut<TResponse, TBody>(path: string, body: TBody): Promise<TResponse> {
  return request<TResponse>(path, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function apiDelete<TResponse>(path: string, body?: unknown): Promise<TResponse> {
  const init: RequestInit = {
    method: 'DELETE',
  }
  if (body !== undefined) {
    init.body = JSON.stringify(body)
  }
  return request<TResponse>(path, init)
}

export async function login(payload: LoginRequest): Promise<ApiResponse<LoginResponse>> {
  return apiPost<ApiResponse<LoginResponse>, LoginRequest>('/auth/login', payload)
}

export async function fetchCurrentUser(): Promise<ApiResponse<AuthUser>> {
  return apiGet<ApiResponse<AuthUser>>('/auth/me')
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<ApiResponse<{ success: boolean }>> {
  return apiPost<ApiResponse<{ success: boolean }>, { current_password: string; new_password: string }>(
    '/auth/change-password',
    { current_password: currentPassword, new_password: newPassword },
  )
}

export async function logout(): Promise<ApiResponse<{ success: boolean }>> {
  return apiPost<ApiResponse<{ success: boolean }>, Record<string, never>>('/auth/logout', {})
}

export interface CRMObjectRow {
  id: string
  tenant_id: string
  name: string
  label: string
  label_plural: string
  icon: string | null
  is_system: number
  is_deleted: number
  ti_header_fields: string | null
  ti_tabs: string | null
}

export async function fetchObjects(): Promise<ApiResponse<CRMObjectRow[]>> {
  return apiGet<ApiResponse<CRMObjectRow[]>>('/objects')
}

export async function fetchObjectSchema(objectId: string): Promise<
  ApiResponse<{
    object: CRMObjectRow
    fields: FieldMeta[]
    object_permission: string
    field_permissions: Record<string, string>
  }>
> {
  return apiGet(`/objects/${objectId}/schema`)
}
